// src/admin/pages/AdminInventory.js
import React, { useEffect, useState, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
    FaBoxOpen,
    FaExclamationTriangle,
    FaTrash,
} from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  getInventoryAlerts,
  deleteInventoryAlert,
} from "../../services/adminInventory.api";

function AdminInventory() {
    const [isOpen, setIsOpen] = useState(false);
    const { lang } = useContext(AdminLanguageContext);

    const TEXT = {
        EN: {
            title: "Inventory Alerts",
            lowBelow: "Low Stock Below:",
            search: "Search product...",
            image: "Product ID",
            product: "Price",
            price: "Quantity",
            stock: "Status",
            status: "Actions",
            actions: "Actions",
            checking: "Checking inventory...",
            noMatch: "No products match your search.",
            out: "Out of Stock",
            low: "Low Stock",
            in: "In Stock",
            deleteTitle: "Delete Product?",
            deleteText: "Are you sure? This action cannot be undone.",
            cancel: "Cancel",
            delete: "Delete",
        },
        AR: {
            title: "تنبيهات المخزون",
            lowBelow: "تنبيه عند الكمية أقل من:",
            search: "ابحث عن منتج...",
            image: "Product ID",
            product: "السعر",
            price: "الكمية",
            stock: "الحالة",
            status: "الإجراءات",
            actions: "الإجراءات",
            checking: "جاري فحص المخزون...",
            noMatch: "لا توجد منتجات مطابقة.",
            out: "نفد المخزون",
            low: "مخزون منخفض",
            in: "متوفر",
            deleteTitle: "حذف المنتج؟",
            deleteText: "هل أنت متأكد؟ لا يمكن التراجع عن هذا الإجراء.",
            cancel: "إلغاء",
            delete: "حذف",
        },
    }[lang];

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [threshold, setThreshold] = useState(5);
    const [deleteId, setDeleteId] = useState(null);
    const [search, setSearch] = useState("");

   useEffect(() => {
  setLoading(true);
  getInventoryAlerts()
    .then(setProducts)
    .finally(() => setLoading(false));
}, []);

    const filtered = products.filter((p) =>
  (p.name_en || "").toLowerCase().includes(search.toLowerCase()) ||
  (p.name_ar || "").toLowerCase().includes(search.toLowerCase()) ||
  (p.public_id || "").toLowerCase().includes(search.toLowerCase())
);


    const getStockStatus = (qty) => {
  if (qty === 0) return "out";
  return "low";
};


    const getRowStyle = (stock) => {
        const status = getStockStatus(stock);
        if (status === "out") return "bg-red-100 text-red-700";
        if (status === "low") return "bg-yellow-100 text-yellow-700";
        return "";
    };

    const confirmDelete = async () => {
  await deleteInventoryAlert(deleteId);
  setProducts((prev) => prev.filter((p) => p.variant_id !== deleteId));
  setDeleteId(null);
};


    return (
        <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
            <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

            <div className="flex-1 lg:ml-64 w-full transition-all duration-300">
                <AdminHeader setIsOpen={setIsOpen} />

                <main className="p-4 sm:p-6 animate-fadeSlide">

                    {/* TITLE + THRESHOLD */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                       

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
  <h1 className="text-2xl font-bold text-cyan-700 flex items-center gap-2">
    <FaBoxOpen /> {TEXT.title}
  </h1>

  {/* SUMMARY BADGES */}
  <div className="flex items-center gap-3">
    <span className="px-4 py-2 rounded-xl bg-yellow-100 text-yellow-800 font-bold text-sm">
      {TEXT.low}: {products.filter(p => p.stock_qty > 0 && p.stock_qty <= 3).length}
    </span>

    <span className="px-4 py-2 rounded-xl bg-red-100 text-red-800 font-bold text-sm">
      {TEXT.out}: {products.filter(p => p.stock_qty === 0).length}
    </span>
  </div>
</div>

                    </div>

                    {/* SEARCH */}
                    <input
                        type="text"
                        placeholder={TEXT.search}
                        className="w-full p-3 border rounded-xl mb-4 shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    {/* DESKTOP TABLE */}
                    <div className="hidden md:block bg-white shadow-lg rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-[850px] w-full">
                                <thead className="bg-gray-200 text-gray-700">
                                    <tr>
                                        <th className="p-3 text-center">{TEXT.image}</th>
                                        <th className="p-3 text-center">{TEXT.product}</th>
                                        <th className="p-3 text-center">{TEXT.price}</th>
                                        <th className="p-3 text-center">{TEXT.stock}</th>
                                        <th className="p-3 text-center">{TEXT.status}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6" className="p-5 text-center">
                                                {TEXT.checking}
                                            </td>
                                        </tr>
                                    ) : filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="p-5 text-center">
                                                {TEXT.noMatch}
                                            </td>
                                        </tr>
                                    ) : (
                                        filtered.map((p) => {
                                           const status = getStockStatus(p.stock_qty);
return (
 <tr key={p.variant_id} className="border-b hover:bg-gray-50">


                                                   <td className="p-3 text-center">
  <div className="font-semibold text-gray-800">
    {lang === "EN" ? p.name_en : p.name_ar}
  </div>

  <div className="text-xs text-gray-500 mt-1">
    {p.public_id} • {p.color_name || "-"} {p.size ? `• ${p.size}` : ""}
  </div>
</td>

<td className="p-3 font-medium text-gray-700 text-center">
  ${p.base_price}
</td>

<td className="p-3 font-bold text-center">
  {p.stock_qty}
</td>


                                                    <td className="p-3 text-center">
                                                        {status === "out" ? (
                                                           <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-bold whitespace-nowrap">
  <FaExclamationTriangle /> {TEXT.out}
</span>

                                                        ) : status === "low" ? (
                                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-bold whitespace-nowrap">
  <FaExclamationTriangle /> {TEXT.low}
</span>

                                                        ) : (
                                                            <span className="px-3 py-1 rounded-full bg-green-200 text-green-800 font-bold">
                                                                {TEXT.in}
                                                            </span>
                                                        )}
                                                    </td>

                                                   <td className="p-3 text-center align-middle">

  <button
    className="text-red-600 hover:text-red-800"
    onClick={() => setDeleteId(p.variant_id)}
  >
    <FaTrash />
  </button>
</td>

                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* MOBILE VIEW */}
<div className="md:hidden space-y-4">
  {loading ? (
    <p className="text-center py-4">{TEXT.checking}</p>
  ) : filtered.length === 0 ? (
    <p className="text-center py-4">{TEXT.noMatch}</p>
  ) : (
    filtered.map((p) => {
      const status = getStockStatus(p.stock_qty);

      return (
        <div
          key={p.variant_id}
          className={`p-4 rounded-xl shadow bg-white border ${getRowStyle(p.stock_qty)}`}
        >
          {/* Name */}
          <p className="font-bold text-lg">
            {lang === "EN" ? p.name_en : p.name_ar}
          </p>

          {/* Public ID + Color + Size */}
          <p className="text-xs text-gray-600 mt-1">
            {p.public_id || "-"}{" "}
            {p.color_name ? `• ${p.color_name}` : ""}{" "}
            {p.size ? `• ${p.size}` : ""}
          </p>

          {/* Price */}
          <p className="text-sm mt-2">
            {lang === "EN" ? "Price" : "السعر"}: <span className="font-semibold">${p.base_price}</span>
          </p>

          {/* Quantity */}
          <p className="text-sm">
            {lang === "EN" ? "Quantity" : "الكمية"}: <span className="font-bold">{p.stock_qty}</span>
          </p>

          {/* Status */}
          <div className="mt-2 font-semibold">
            {status === "out" ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-bold whitespace-nowrap">
                <FaExclamationTriangle /> {TEXT.out}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-bold whitespace-nowrap">
                <FaExclamationTriangle /> {TEXT.low}
              </span>
            )}
          </div>

          {/* Delete */}
          <div className="mt-3">
            <button
              className="text-red-600 hover:text-red-800 flex items-center gap-2 text-sm font-semibold"
              onClick={() => setDeleteId(p.variant_id)}
            >
              <FaTrash /> {TEXT.delete}
            </button>
          </div>
        </div>
      );
    })
  )}
</div>

        


                    {/* DELETE MODAL */}
                    {deleteId !== null && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
                            <div className="bg-white p-6 rounded-xl shadow-xl w-80">
                                <h2 className="text-lg font-bold mb-4 text-red-600">
                                    {TEXT.deleteTitle}
                                </h2>
                                <p className="text-gray-600 mb-6">
                                    {TEXT.deleteText}
                                </p>

                                <div className="flex justify-end gap-3">
                                    <button
                                        className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400"
                                        onClick={() => setDeleteId(null)}
                                    >
                                        {TEXT.cancel}
                                    </button>

                                    <button
                                        className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                                        onClick={confirmDelete}
                                    >
                                        {TEXT.delete}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </main>
            </div>
        </div>
    );
}

export default AdminInventory;
